import queryString from 'query-string';
import useSWR from 'swr';
import { useCallback, useEffect, useState } from 'react';
import useQuery from './useQuery';

const apiUrl = process.env.GATSBY_PERFSCAN_URL || 'https://perf.services.hipex.cloud';

function isMultipleTestFinished(data) {
  return data.every((testRequest) => testRequest.tests.length > 0 && testRequest.tests[0].finished);
}

function isSingleTestFinished(data) {
  if (data.tests.length === 0) {
    return false;
  }

  return data.tests.every((test) => test.finished);
}

export function isTestFinished(data = []) {
  if (data.length === 1) {
    return isSingleTestFinished(data[0]);
  }
  if (data.length > 0) {
    return isMultipleTestFinished(data);
  }

  return false;
}

export function isTestRequestFinished(data = []) {
  if (data.length === 1) {
    return data[0].finished;
  }
  if (data.length > 0) {
    return data.every((testRequest) => testRequest.finished);
  }
  return false;
}

async function fetchScan(key) {
  if (key.match(/[a-z]+\/false/)) {
    return {};
  }

  const result = await fetch(`${apiUrl}/test_requests/${key}`);
  const obj = {
    status: result.status,
  };
  switch (result.status) {
    case 200:
      obj.body = await result.json();
      break;
    case 404:
      obj.error = `Performance Scan with ID "${key}" not found`;
      break;
    default:
      obj.error = 'An unknown error occurred';
      break;
  }
  return obj;
}

export function useScan(id) {
  const params = global?.location?.search
    ? queryString.parse(global.location.search)
    : false;
  return fetchScan(id ?? params.scan);
}

export function useScans(scanCount = undefined) {
  const [data, updateData] = useState([]);
  const [errors, updateErrors] = useState(null);
  const [loading, setLoading] = useState(true);

  const testRequestIds = useQuery('scan');

  const loadData = useCallback(async () => {
    const results = [];
    const ids = [...testRequestIds];
    if (scanCount && ids.length > scanCount) {
      ids.length = scanCount;
    }

    // eslint-disable-next-line no-restricted-syntax
    for await (const testRequestId of ids) {
      const result = await fetchScan(testRequestId);
      results.push(result);
    }
    return results;
  }, [scanCount, testRequestIds]);

  useEffect(() => {
    const timer = setInterval(async () => {
      const results = await loadData();
      if (results.length === 0) {
        return;
      }

      const errorMap = results.map((value) => value.error).filter((error) => !!error);
      if (errorMap.length > 0) {
        clearInterval(timer);
        updateErrors(errorMap);
        setLoading(false);
        return;
      }

      const testRequests = results.map((result) => result.body);

      updateData(testRequests);

      if (isTestRequestFinished(testRequests)) {
        clearInterval(timer);
        setLoading(false);
      }
    }, 3500);

    return () => clearInterval(timer);
  }, [loadData]);

  return { data, errors, loading };
}

export function useTop(limit = 10) {
  return useSWR(`test_runs?limit=${limit}`, fetchScan);
}

export function useAvgs(industry = 'all') {
  return useSWR(`stats/industry/${industry}/global`, fetchScan);
}

export function useStats(url) {
  return useSWR(`stats/url/${url}`, fetchScan);
}

export function usePercentiles(industry = 'all') {
  return useSWR(`stats/percentiles/${industry}/global`, fetchScan);
}

export default {
  useScan,
};
